<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-4 sm:px-6 max-w-[1700px] mx-auto">

    <!-- Page header -->
    <div class="flex flex-row items-end justify-between gap-4 mb-6 flex-wrap">
      <div>
        <div class="text-xs text-gray-400 font-medium">Purchasing</div>
        <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Purchase orders</h1>
        <p class="text-sm text-gray-500 mt-1">Stock ordered from suppliers - what's arrived, and what's still to be paid</p>
      </div>
      <div class="flex items-center gap-2 flex-wrap">
      <a [routerLink]="['/shipments']" class="group flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold shadow-sm hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800 transition-colors" matTooltip="Containers on their way, by voyage">
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px] text-gray-400 group-hover:text-amber-600">directions_boat</mat-icon>Shipments
        <mat-icon class="!text-[16px] !h-4 !w-4 text-gray-300 group-hover:text-amber-600 -mr-1">arrow_forward</mat-icon>
      </a>
      <button
        class="group flex items-center gap-2.5 pl-2.5 pr-5 py-2 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm shadow-md shadow-amber-200 hover:shadow-lg hover:shadow-amber-300 hover:from-amber-300 hover:to-amber-400 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all focus:outline-none focus:ring-4 focus:ring-amber-200"
        (click)="onCreatePurchaseOrder()">
        <span class="h-8 w-8 rounded-lg bg-neutral-900 text-amber-400 flex items-center justify-center transition-transform group-hover:rotate-90 duration-200">
          <mat-icon class="!text-[20px] !h-5 !w-5">add</mat-icon>
        </span>
        New purchase order
      </button>
      </div>
    </div>

    <!-- Headline figures - the last three are shortcuts to the orders they count -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div class="relative overflow-hidden rounded-2xl bg-neutral-900 text-white p-5 shadow-sm">
        <mat-icon class="absolute -right-3 -bottom-4 !text-[96px] !h-24 !w-24 !text-white/5 pointer-events-none">local_shipping</mat-icon>
        <div class="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Open orders</div>
        <div class="text-3xl font-bold mt-1.5 leading-none po-num-tab">{{ summary.open }}</div>
        <div class="text-xs text-neutral-400 mt-2">Ordered, still to arrive in full</div>
      </div>

      <button class="kpi group text-left rounded-2xl bg-white border p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" [ngClass]="lateOnly ? 'border-red-300 ring-4 ring-red-50' : 'border-gray-100'" (click)="showLate()">
        <div class="flex items-center justify-between">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Late deliveries</span>
          <span class="h-8 w-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">schedule</mat-icon></span>
        </div>
        <div class="text-3xl font-bold mt-1 leading-none" [ngClass]="summary.late ? 'text-red-600' : 'text-gray-900'">{{ summary.late }}</div>
        <div class="text-xs text-gray-400 mt-2 flex items-center gap-1 group-hover:text-red-600 transition-colors">Past expected delivery <mat-icon class="!text-[14px] !h-3.5 !w-3.5">arrow_forward</mat-icon></div>
      </button>

      <button class="kpi group text-left rounded-2xl bg-white border p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" [ngClass]="billingStatus === 'BILLED' && !lateOnly ? 'border-indigo-300 ring-4 ring-indigo-50' : 'border-gray-100'" (click)="showAwaitingPayment()">
        <div class="flex items-center justify-between">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-gray-400">To pay</span>
          <span class="h-8 w-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">request_quote</mat-icon></span>
        </div>
        <div class="text-3xl font-bold mt-1 leading-none text-gray-900">{{ summary.awaitingPayment }}</div>
        <div class="text-xs text-gray-400 mt-2 flex items-center gap-1 group-hover:text-indigo-600 transition-colors">Billed, awaiting payment <mat-icon class="!text-[14px] !h-3.5 !w-3.5">arrow_forward</mat-icon></div>
      </button>

      <button class="kpi group text-left rounded-2xl bg-white border p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" [ngClass]="billingStatus === 'OVERDUE' && !lateOnly ? 'border-rose-300 ring-4 ring-rose-50' : 'border-gray-100'" (click)="showOverdue()">
        <div class="flex items-center justify-between">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Overdue</span>
          <span class="h-8 w-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">priority_high</mat-icon></span>
        </div>
        <div class="text-3xl font-bold mt-1 leading-none" [ngClass]="summary.overdue ? 'text-rose-600' : 'text-gray-900'">{{ summary.overdue }}</div>
        <div class="text-xs text-gray-400 mt-2 flex items-center gap-1 group-hover:text-rose-600 transition-colors">Payment overdue <mat-icon class="!text-[14px] !h-3.5 !w-3.5">arrow_forward</mat-icon></div>
      </button>
    </div>

    @if (loadError) {
      <div class="mb-5 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-sm flex items-center justify-between gap-3">
        <span class="flex items-center gap-2"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">cloud_off</mat-icon>Couldn't load purchase orders from the server.</span>
        <button class="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-red-200 hover:bg-red-100" (click)="loadPurchaseOrders()">Try again</button>
      </div>
    }

    <!-- List card -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

      <!-- Status tabs -->
      <div class="px-3 sm:px-5 border-b border-gray-100 overflow-x-auto">
        <div class="flex items-center gap-1 min-w-max">
          @for (tab of statusTabs; track tab) {
          <button class="relative flex items-center gap-2 px-3 py-3.5 text-sm font-semibold transition-colors whitespace-nowrap"
            [ngClass]="status === tab ? 'text-gray-900' : 'text-gray-500 hover:text-gray-800'"
            (click)="setStatus(tab)">
            @if (tab !== 'ALL') { <span class="h-2 w-2 rounded-full" [ngClass]="statusDot(tab)"></span> }
            <span class="capitalize">{{ tab === 'ALL' ? 'All orders' : (tab | lowercase) }}</span>
            <span class="rounded-full px-1.5 min-w-[22px] text-center text-[11px] po-num-tab" [ngClass]="status === tab ? 'bg-neutral-900 text-amber-400' : 'bg-gray-100 text-gray-500'">{{ tabCount(tab) }}</span>
            @if (status === tab) { <span class="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-amber-400"></span> }
          </button>
          }
        </div>
      </div>

      <!-- Search + billing -->
      <div class="px-4 sm:px-5 py-4 flex flex-col lg:flex-row lg:items-center gap-3 border-b border-gray-100">
        <div class="relative flex-1">
          <mat-icon class="absolute left-3.5 top-1/2 -translate-y-1/2 !text-gray-400 !text-[20px] !h-5 !w-5 pointer-events-none">search</mat-icon>
          <input
            #searchBox
            type="text"
            placeholder="Search by PO reference, supplier or supplier document (e.g. INVOICE#07)"
            class="w-full pl-11 pr-16 py-2.5 rounded-xl border border-gray-200 bg-gray-50 hover:border-gray-300 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm"
            [(ngModel)]="search"
            (ngModelChange)="onSearchInput()"
            (keydown.enter)="applyFilters()"
            (keydown.escape)="clearSearch()"
          />
          @if (search) {
          <button class="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="clearSearch()" aria-label="Clear search">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
          </button>
          } @else {
          <kbd class="hidden sm:block absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-gray-400 border border-gray-200 border-b-2 rounded-md px-1.5 py-0.5 bg-white pointer-events-none" title="Press / to search">/</kbd>
          }
        </div>

        <div class="flex items-center gap-2 flex-wrap">
          <div class="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
            <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-2 hidden xl:block">Billing</span>
            @for (b of billingOptions; track b) {
            <button class="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize"
              [ngClass]="billingStatus === b ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'"
              (click)="setBilling(b)">{{ b === 'ALL' ? 'Any' : (b | lowercase) }}</button>
            }
          </div>
          @if (filtersActive) {
          <button class="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-600 px-2.5 py-2 rounded-lg hover:bg-red-50 transition-colors" (click)="onResetFilters()">
            <mat-icon class="!text-[16px] !h-4 !w-4">filter_alt_off</mat-icon> Clear all
          </button>
          }
        </div>
      </div>

      @if (lateOnly) {
      <div class="px-5 py-2.5 bg-red-50/60 border-b border-red-100 flex items-center gap-2 text-xs">
        <mat-icon class="!text-[16px] !h-4 !w-4 !text-red-500">schedule</mat-icon>
        <span class="font-semibold text-red-700">Showing orders past their expected delivery</span>
        <button class="ml-auto font-semibold text-red-700 hover:text-red-900 flex items-center gap-0.5" (click)="lateOnly = false; applyFilters()">
          Show all <mat-icon class="!text-[14px] !h-3.5 !w-3.5">close</mat-icon>
        </button>
      </div>
      }

      <!-- Column headings (wide screens) -->
      <div class="hidden lg:grid po-list-cols gap-4 pl-7 pr-5 py-2.5 bg-gray-50/80 border-b border-gray-100 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
        <div>Order</div><div>Supplier</div><div>Delivery</div><div>Received</div><div class="text-right">Total</div><div>Status</div><div></div>
      </div>

      @if (loading && orders.length === 0) {
        <!-- Skeleton rows -->
        @for (r of skeletonRows; track r) {
        <div class="lg:grid po-list-cols gap-4 pl-7 pr-5 py-4 border-b border-gray-50 items-center animate-pulse">
          <div><div class="h-3.5 w-24 bg-gray-200 rounded"></div><div class="h-2.5 w-16 bg-gray-100 rounded mt-2"></div></div>
          <div class="flex items-center gap-3 mt-3 lg:mt-0"><div class="h-9 w-9 rounded-xl bg-gray-100"></div><div><div class="h-3.5 w-32 bg-gray-200 rounded"></div><div class="h-2.5 w-20 bg-gray-100 rounded mt-2"></div></div></div>
          <div class="hidden lg:block"><div class="h-3 w-20 bg-gray-100 rounded"></div></div>
          <div class="hidden lg:block"><div class="h-1.5 w-full bg-gray-100 rounded"></div></div>
          <div class="hidden lg:block"><div class="h-3.5 w-16 bg-gray-200 rounded ml-auto"></div></div>
          <div class="hidden lg:block"><div class="h-5 w-20 bg-gray-100 rounded-full"></div></div>
          <div></div>
        </div>
        }
      } @else if (orders.length === 0) {
        <div class="min-h-80 flex flex-col justify-center items-center text-center px-6 py-14">
          <span class="h-14 w-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-3"><mat-icon class="!text-amber-500 !text-[30px] !h-[30px] !w-[30px]">{{ filtersActive ? 'search_off' : 'inventory_2' }}</mat-icon></span>
          <div class="text-lg font-semibold text-gray-800">{{ filtersActive ? 'No purchase orders match' : 'No purchase orders yet' }}</div>
          <div class="text-sm text-gray-500 mt-1 max-w-sm">{{ filtersActive ? 'Try a different search, or clear the filters to see everything.' : 'Raise the first order to a supplier to start tracking stock on the way.' }}</div>
          @if (filtersActive) {
          <button class="mt-4 px-5 py-2.5 rounded-xl bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-700" (click)="onResetFilters()">Clear filters</button>
          } @else {
          <button class="mt-4 px-5 py-2.5 rounded-xl bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-700" (click)="onCreatePurchaseOrder()">New purchase order</button>
          }
        </div>
      } @else {

      <!-- Rows -->
      <div class="transition-opacity" [class.opacity-50]="loading">
        @for (po of orders; track po.id) {
        <div class="po-list-row group relative lg:grid po-list-cols gap-4 pl-7 pr-5 py-4 items-center border-b border-gray-50 last:border-b-0 hover:bg-amber-50/40 cursor-pointer transition-colors"
          tabindex="0" role="link" [attr.aria-label]="'Open ' + po.reference"
          (click)="onView(po)" (keydown.enter)="onView(po)">
          <!-- Status accent -->
          <span class="absolute left-0 top-3 bottom-3 w-1 rounded-r-full" [ngClass]="statusDot(po.status)"></span>

          <!-- Order -->
          <div class="min-w-0 flex items-start justify-between lg:block gap-3">
            <div class="min-w-0">
              <div class="font-bold text-gray-900 group-hover:text-amber-700 transition-colors">{{ po.reference }}</div>
              @if (po.sourceDocument) {
              <div class="mt-0.5 inline-flex items-center gap-1 text-[11px] text-gray-500 max-w-full" [matTooltip]="'Supplier document: ' + po.sourceDocument">
                <mat-icon class="!text-[12px] !h-3 !w-3 !text-gray-400 flex-shrink-0">receipt_long</mat-icon><span class="truncate font-mono">{{ po.sourceDocument }}</span>
              </div>
              } @else {
              <div class="text-[11px] text-gray-300 mt-0.5">{{ po.orderDate | date:'d MMM yyyy' }}</div>
              }
            </div>
            <!-- Total on small screens -->
            <div class="lg:hidden text-right leading-tight flex-shrink-0">
              <div class="font-bold text-gray-900 po-num-tab">{{ currencySymbol(po.currency) }}{{ po.totalCost | number:'1.2-2' }}</div>
              <div class="text-[11px] text-gray-400">{{ po.currency }}</div>
            </div>
          </div>

          <!-- Supplier -->
          <div class="min-w-0 flex items-center gap-3 mt-3 lg:mt-0">
            <span class="h-9 w-9 rounded-xl bg-amber-100 text-amber-800 font-bold text-[11px] flex items-center justify-center flex-shrink-0">{{ supplierInitials(po) }}</span>
            <div class="min-w-0">
              <div class="font-semibold text-gray-900 truncate" [title]="getSupplierName(po)">{{ getSupplierName(po) }}</div>
              <div class="text-xs text-gray-400 truncate flex items-center gap-1">
                <mat-icon class="!text-[12px] !h-3 !w-3">warehouse</mat-icon>{{ po.store }}
                @if (po.buyerName) { <span class="text-gray-300">&middot;</span> {{ po.buyerName }} }
              </div>
            </div>
          </div>

          <!-- Delivery -->
          <div class="text-sm mt-3 lg:mt-0 flex items-center gap-3 lg:block">
            <div class="text-gray-700 hidden lg:block">{{ po.orderDate | date:'d MMM yyyy' }}</div>
            @if (daysUntilDue(po); as d) {
              @if (d < 0) {
              <span class="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 mt-0.5"><mat-icon class="!text-[12px] !h-3 !w-3">schedule</mat-icon>{{ -d }} day{{ d === -1 ? '' : 's' }} late</span>
              } @else if (d <= 7) {
              <span class="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mt-0.5">Due in {{ d }} day{{ d === 1 ? '' : 's' }}</span>
              } @else {
              <span class="text-xs text-gray-400">Due {{ po.expectedDeliveryDate | date:'d MMM' }}</span>
              }
            } @else {
              @if (daysUntilDue(po) === 0) {
              <span class="inline-flex items-center text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mt-0.5">Due today</span>
              } @else if (po.expectedDeliveryDate) {
              <span class="text-xs text-gray-400">Due {{ po.expectedDeliveryDate | date:'d MMM yyyy' }}</span>
              } @else {
              <span class="text-xs text-gray-300">No delivery date</span>
              }
            }
          </div>

          <!-- Received -->
          <div class="mt-3 lg:mt-0">
            @if (po.itemCount) {
            <div class="flex items-center justify-between text-xs mb-1.5">
              <span class="text-gray-600 font-semibold po-num-tab">{{ po.receivedCount }} <span class="text-gray-400 font-normal">/ {{ po.itemCount }}</span></span>
              <span class="font-semibold po-num-tab" [ngClass]="receivedPct(po) >= 100 ? 'text-emerald-600' : 'text-gray-400'">{{ receivedPct(po) }}%</span>
            </div>
            <div class="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div class="h-full rounded-full transition-all" [ngClass]="receivedPct(po) >= 100 ? 'bg-emerald-500' : 'bg-amber-400'" [style.width.%]="receivedPct(po)"></div>
            </div>
            } @else {
            <span class="text-xs text-gray-300">No items yet</span>
            }
          </div>

          <!-- Total (wide screens) -->
          <div class="hidden lg:block text-right leading-tight">
            <div class="font-bold text-gray-900 po-num-tab">{{ currencySymbol(po.currency) }}{{ po.totalCost | number:'1.2-2' }}</div>
            <div class="text-[11px] text-gray-400">{{ po.currency }} &middot; inc. tax</div>
          </div>

          <!-- Status -->
          <div class="flex flex-row lg:flex-col items-start gap-1 mt-3 lg:mt-0">
            <span [ngClass]="getStatusClass(po.status)" class="rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize whitespace-nowrap">{{ po.status | lowercase }}</span>
            <span [ngClass]="getBillingStatusClass(po.billingStatus)" class="rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize whitespace-nowrap">{{ po.billingStatus | lowercase }}</span>
          </div>

          <!-- Actions -->
          <div class="absolute right-3 bottom-3 lg:static flex justify-end">
            <button class="h-9 w-9 rounded-full text-gray-400 hover:bg-white hover:text-gray-800 hover:shadow-sm flex items-center justify-center transition-all"
              [matMenuTriggerFor]="rowMenu" (click)="$event.stopPropagation()" (keydown.enter)="$event.stopPropagation()" aria-label="Order actions" matTooltip="Actions">
              <mat-icon class="!text-[20px]">more_horiz</mat-icon>
            </button>
            <mat-menu #rowMenu="matMenu" xPosition="before">
              <button mat-menu-item (click)="onView(po)"><mat-icon>edit_square</mat-icon><span>Open order</span></button>
              @if (po.supplierId) {
              <button mat-menu-item (click)="onViewSupplier(po)"><mat-icon>storefront</mat-icon><span>Open supplier</span></button>
              <button mat-menu-item (click)="onNewForSupplier(po)"><mat-icon>add_shopping_cart</mat-icon><span>New PO for this supplier</span></button>
              }
            </mat-menu>
          </div>
        </div>
        }
      </div>

      <mat-paginator [pageSizeOptions]="[10, 20, 50]"
                     showFirstLastButtons
                     [length]="count"
                     [pageIndex]="pageNumber"
                     [pageSize]="itemsPerPage"
                     (page)="updateList($event)">
      </mat-paginator>
      }
    </div>

  </div>
  </div>
</app-main-layout>
